import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  SvgIcon,
  Switch,
  Typography,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import { NotificationService } from 'services/NotificationService';
import { Toast, Utils } from 'shared/helpers';
import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Formik, useFormikContext } from 'formik';
import { AppFormikTextField } from 'shared/components/AppComponents';

export interface SubModule {
  moduleId: string;
  enabled: boolean;
  _id: string;
  module: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  event: string;
}

export interface Cycle {
  daily: boolean;
  weekly: boolean;
  monthly: boolean;
}

export interface INotificationProps {
  _id: string;
  organizationId: any;
  type: string;
  subModules: SubModule[];
  module: string;
  descriptions: string;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  event: string;
  userId: any;
  emailCycle: Cycle;
  inAppCycle: Cycle;
  isExpanded: boolean;
  searchMatched: boolean;
}

const SearchFormikContext = ({ onChange }: any): any => {
  const { values } = useFormikContext();

  useEffect(() => {
    onChange(values);
  }, [values]);

  return null;
};

const NotificationSetting = (props: any): ReactElement => {
  const { selectedNote } = props;
  const [notificationList, setNotificationList] = useState<
    INotificationProps[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const UpdatedNotes = (title: string, tag: string): boolean => {
    if (tag === 'AI Inventory') {
      return title?.includes(tag);
    }
    if (tag === title) {
      return true;
    }
    return false;
  };

  // API Calls
  const getNotificationList = (): void => {
    setIsLoading(true);
    NotificationService.getallNotificationSettings()
      .then((res) => {
        const { data } = res.data;
        const notifications = data
          .map((d: INotificationProps) => ({
            ...d,
            searchMatched: true,
            isExpanded: selectedNote
              ? UpdatedNotes(d.module, selectedNote?.tag)
              : false,
          }))
          .sort((a: INotificationProps, b: INotificationProps) =>
            a.module.localeCompare(b.module)
          );
        setNotificationList(notifications);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  console.log(notificationList);
  const actionOnUpdate = (item: INotificationProps): void => {
    const requestObj = {
      moduleId: item._id,
      inAppEnabled: item.inAppEnabled,
      emailEnabled: item.emailEnabled,
      subModules: item.subModules.map((sm) => ({
        moduleId: sm._id,
        enabled: sm.enabled,
      })),
      emailCycle: item.emailCycle,
      inAppCycle: item.inAppCycle,
    };

    NotificationService.updateNotificationSetting(requestObj)
      .then((res) => {
        const { data } = res.data;
        const updatedList = notificationList.map((n) =>
          n._id === item._id ? { ...n, _id: data._id } : n
        );
        setNotificationList(updatedList);
        Toast.showSuccessMessage('Notification preference updated.');
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  // Hooks
  useEffect(() => {
    getNotificationList();
  }, []);

  // Ui Actions
  const handleAccordionToggle = (index: number): void => {
    setNotificationList((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              isExpanded: !item.isExpanded,
            }
          : item
      )
    );
  };
  const handleInAppToggle = (index: number): void => {
    const updatedList = [...notificationList];
    notificationList[index].inAppEnabled =
      !notificationList[index].inAppEnabled;
    setNotificationList(updatedList);
    actionOnUpdate(updatedList[index]);
  };

  const handleEmailToggle = (index: number): void => {
    const updatedList = [...notificationList];
    updatedList[index].emailEnabled = !updatedList[index].emailEnabled;
    actionOnUpdate(updatedList[index]);
  };

  const handleSubModuleCheck = (
    moduleIndex: number,
    subModuleIndex: number
  ): void => {
    const updatedList = [...notificationList];

    updatedList[moduleIndex].subModules[subModuleIndex].enabled =
      !updatedList[moduleIndex].subModules[subModuleIndex].enabled;
    setNotificationList(updatedList);
    actionOnUpdate(updatedList[moduleIndex]);
  };

  const handleCheckBoxUpdate = (
    index: number,
    label: keyof Cycle,
    cycle: 'inAppCycle' | 'emailCycle'
  ): void => {
    const updatedList = [...notificationList];
    updatedList[index][cycle][label] = !updatedList[index][cycle][label];
    setNotificationList(updatedList);
    actionOnUpdate(updatedList[index]);
  };

  const handleFilterChange = (values: any): any => {
    const { search } = values;
    const tList = notificationList.map((item) => {
      let matchesSearch = true;
      if (search)
        matchesSearch = item.module
          .toLowerCase()
          .includes(search.toLowerCase());
      return { ...item, searchMatched: matchesSearch };
    });
    setNotificationList(tList);
  };

  const getAppData = (
    index: number,
    label: keyof Cycle,
    cycle: 'inAppCycle' | 'emailCycle'
  ): ReactElement => (
    <FormControlLabel
      control={
        <Checkbox
          sx={{
            p: 0.5,
            color: '#BDBDBD',
            '&.Mui-checked': {
              color: '#111111',
            },
          }}
          checked={notificationList[index]?.[cycle]?.[label] ?? false}
          onChange={() => handleCheckBoxUpdate(index, label, cycle)}
        />
      }
      label={label.charAt(0).toUpperCase() + label.slice(1)}
    />
  );

  // UI Elements
  return (
    <>
      <Box
        className="notifications-head-wrapper"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box className="compliance-title">
          <Typography variant="h4">Feature Alert Configuration</Typography>
          <Typography component="span">
            Configure detailed alerting for specific features.
          </Typography>
        </Box>

        <Box className="search-risk-wrapper">
          <Formik
            initialValues={{ search: '' }}
            onSubmit={() => {
              console.log('search');
            }}
          >
            <>
              <SearchFormikContext onChange={handleFilterChange} />
              <AppFormikTextField
                outerLabel={false}
                name="search"
                placeholder="Search features.."
                inputProps={{
                  startAdornment: (
                    <SvgIcon
                      component={Search}
                      inheritViewBox
                      className="view-icon mr-2"
                      fontSize="inherit"
                      htmlColor="#76A81F"
                    />
                  ),
                }}
              />
            </>
          </Formik>
        </Box>
      </Box>

      {notificationList.length > 0 && (
        <Box className="notification-settings">
          {notificationList.map((item, index) => {
            const subModules = item.subModules ?? [];
            const checkedCount = item.subModules.filter(
              (sm) => sm.enabled
            ).length;

            return (
              <>
                {item.searchMatched && (
                  <Accordion
                    key={item._id}
                    disableGutters
                    elevation={0}
                    className="accord-container "
                    expanded={item.isExpanded}
                    onChange={() => handleAccordionToggle(index)}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 3,
                        py: 1.5,
                        '& .MuiAccordionSummary-content': {
                          m: 0,
                          overflow: 'hidden',
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': { ml: 1 },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        gap={2}
                        sx={{ minWidth: 0 }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="h5"
                            className="fw-6"
                            color="text.primary"
                            noWrap
                          >
                            {item.module}
                          </Typography>
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.descriptions}
                          </Typography>
                        </Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={0.5}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <NotificationsNoneOutlinedIcon
                            sx={{ color: 'text.secondary' }}
                          />
                          <Typography variant="body1" color="text.secondary">
                            In-app
                          </Typography>
                          <Switch
                            checked={item.inAppEnabled}
                            onChange={() => {
                              handleInAppToggle(index);
                            }}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={0.5}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <EmailOutlinedIcon sx={{ color: 'text.secondary' }} />
                          <Typography variant="body1" color="text.secondary">
                            Email
                          </Typography>
                          <Switch
                            checked={item.emailEnabled}
                            onChange={() => {
                              handleEmailToggle(index);
                            }}
                          />
                        </Stack>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: 3, pt: 0, pb: 2 }}>
                      {subModules.length > 0 && (
                        <>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="h6"
                              className="fw-6 mt-3"
                              color="text.primary"
                            >
                              Metrics to include in rollups
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              Select which data points appear in your digest
                              reports.
                            </Typography>
                          </Box>

                          <Stack spacing={3} className="mt-3 mb-3">
                            {subModules.map((sm, sIndex) => (
                              <FormControlLabel
                                key={sm._id}
                                control={
                                  <Checkbox
                                    checked={sm.enabled}
                                    onChange={() => {
                                      handleSubModuleCheck(index, sIndex);
                                    }}
                                    sx={{
                                      color: 'text.secondary',
                                      '&.Mui-checked': {
                                        color: 'text.primary',
                                      },
                                      p: 0.75,
                                    }}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      className={sm.enabled ? 'fw-6' : 'fw-4'}
                                      color="text.primary"
                                    >
                                      {sm.module}
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {sm.description}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ mx: 0, alignItems: 'flex-start' }}
                              />
                            ))}
                          </Stack>

                          <Divider sx={{ my: 2 }} />
                        </>
                      )}
                      <div className="mt-3 mb-3">
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Stack direction="row" alignItems="center" gap={1}>
                            <NotificationsNoneOutlinedIcon
                              sx={{ color: 'text.secondary' }}
                            />

                            <Typography
                              variant="h6"
                              className="fw-6"
                              color="text.primary"
                            >
                              In-app notifications
                            </Typography>
                          </Stack>
                          <Switch
                            checked={item.inAppEnabled}
                            onChange={() => {
                              handleInAppToggle(index);
                            }}
                          />
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={2}>
                          {getAppData(index, 'daily', 'inAppCycle')}
                          {getAppData(index, 'weekly', 'inAppCycle')}
                          {getAppData(index, 'monthly', 'inAppCycle')}
                        </Stack>
                      </div>
                      <div className="mt-3 mb-3">
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Stack direction="row" alignItems="center" gap={1}>
                            <EmailOutlinedIcon
                              sx={{ color: 'text.secondary' }}
                            />
                            <Typography
                              variant="h6"
                              className="fw-6"
                              color="text.primary"
                            >
                              Email rollups
                            </Typography>
                          </Stack>
                          <Switch
                            checked={item.emailEnabled}
                            onChange={() => {
                              handleEmailToggle(index);
                            }}
                          />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          {getAppData(index, 'daily', 'emailCycle')}
                          {getAppData(index, 'weekly', 'emailCycle')}
                          {getAppData(index, 'monthly', 'emailCycle')}
                        </Stack>
                      </div>

                      <Divider sx={{ mt: 2, mb: 1.5 }} />

                      <Box>
                        <Typography
                          variant="h6"
                          className="fw-6"
                          color="text.primary"
                          gutterBottom
                        >
                          Summary
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {checkedCount} of {subModules.length} metrics
                          selected.
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            );
          })}
        </Box>
      )}
      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && notificationList.length === 0 && (
        <Card className="empty-content">
          <Typography variant="h5" className="fw-6">
            Notification Setting is Empty.
          </Typography>
        </Card>
      )}
    </>
  );
};

export default NotificationSetting;
